import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Bookmark, Package, ShoppingBag } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ProductCard } from "../components/ProductCard";
import { RequestCard } from "../components/RequestCard";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { hydrateSaves } from "../components/SaveButton";

export default function SavedPage() {
  const navigate = useNavigate();
  const [data, setData] = useState({ products: [], requests: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/saves");
      setData({ products: res.data.products || [], requests: res.data.requests || [] });
      await hydrateSaves();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-6 sm:py-10 md:py-14">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-btn"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6 sm:mb-8"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      <div className="mb-8">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-[10px] tracking-[0.2em] uppercase font-bold mb-3">
          <Bookmark className="h-3 w-3" />
          Saved
        </div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
          Your saved items.
        </h1>
        <p className="mt-3 text-sm sm:text-base text-muted-foreground">
          Products and buy requests you bookmarked for later.
        </p>
      </div>

      <Tabs defaultValue="products" className="w-full">
        <TabsList className="bg-white border border-border rounded-md p-1 h-auto w-full sm:w-auto flex-nowrap overflow-x-auto scrollbar-none justify-start">
          <TabsTrigger data-testid="saved-tab-products" value="products" className="rounded-sm data-[state=active]:bg-foreground data-[state=active]:text-background gap-2 px-4 py-2">
            <ShoppingBag className="h-3.5 w-3.5" /> Products
            <span className="font-mono-brand text-[10px] opacity-70">{data.products.length}</span>
          </TabsTrigger>
          <TabsTrigger data-testid="saved-tab-requests" value="requests" className="rounded-sm data-[state=active]:bg-foreground data-[state=active]:text-background gap-2 px-4 py-2">
            <Package className="h-3.5 w-3.5" /> Buy requests
            <span className="font-mono-brand text-[10px] opacity-70">{data.requests.length}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="mt-6">
          {data.products.length === 0 ? (
            <Empty cta={() => navigate("/products")} text="You haven't saved any products yet." btn="Browse products" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {data.products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="requests" className="mt-6">
          {data.requests.length === 0 ? (
            <Empty cta={() => navigate("/")} text="You haven't saved any buy requests yet." btn="Browse requests" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {data.requests.map((r, i) => (
                <RequestCard key={r.id} req={r} index={i} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Empty({ cta, text, btn }) {
  return (
    <div className="rounded-md border-2 border-dashed border-border bg-white p-10 text-center">
      <Bookmark className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" strokeWidth={1.5} />
      <p className="text-sm text-muted-foreground mb-4">{text}</p>
      <Button onClick={cta} className="rounded-md">{btn}</Button>
    </div>
  );
}
